import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate, useNavigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import IosInstallBanner from "@/components/IosInstallBanner";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@closetapparel.in");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2">
      <IosInstallBanner />
      {/* Left: form */}
      <div className="flex flex-col justify-between p-6 sm:p-8 lg:p-16 relative min-h-screen">
        <div className="flex items-center gap-3">
          <img src="/brand/closset-logo.webp" alt="Closet Apparels" className="w-14 h-14 border-2 border-[#2F343A] bg-white object-contain p-1" />
          <div>
            <div className="font-display font-black text-sm tracking-tight text-[#2F343A]">CLOS<span className="text-[#7A3E1F]">E</span>T APPARELS</div>
            <div className="font-mono-ds text-[10px] tracking-widest text-zinc-500">INVENTORY · v1.0</div>
          </div>
        </div>

        <div className="max-w-md w-full mx-auto py-8 sm:py-12">
          <div className="brut-badge mb-6">SECURE ACCESS</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-3">
            Sign in to<br />the warehouse.
          </h1>
          <p className="text-zinc-600 mb-8 text-sm">Manage 240 GSM tees, polos, and every drop in one ledger.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="font-mono-ds text-[11px] tracking-widest uppercase block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                className="brut-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="font-mono-ds text-[11px] tracking-widest uppercase block mb-2">Password</label>
              <PasswordInput
                testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="brut-card-flat p-3 bg-[#FF3333] text-white font-mono-ds text-xs uppercase tracking-wider">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={submitting}
              className="brut-btn px-6 py-3 w-full disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Enter Warehouse →"}
            </button>
          </form>
        </div>

        <div className="font-mono-ds text-[10px] tracking-widest text-zinc-400 uppercase">
          240 GSM · MADE IN INDIA · {new Date().getFullYear()}
        </div>
      </div>

      {/* Right: editorial panel */}
      <div className="hidden lg:flex relative bg-black text-white p-16 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 brut-stripes opacity-[0.06]" />
        <div className="relative z-10">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">VOL.01 / SS26</div>
        </div>
        <div className="relative z-10">
          <div className="font-display text-7xl font-black leading-[0.9] tracking-tighter">
            HEAVY<br />COTTON.<br />HEAVIER<br />NUMBERS.
          </div>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            <Stat label="GSM" value="240" />
            <Stat label="SIZES" value="XS-XXL" />
            <Stat label="ORDERS" value="9.4K+" />
          </div>
        </div>
        <div className="relative z-10 font-mono-ds text-[10px] tracking-widest uppercase opacity-70">
          A control room for closetapparelindia.com
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-2 border-white p-3">
      <div className="font-mono-ds text-[10px] tracking-widest uppercase opacity-70">{label}</div>
      <div className="font-mono-ds text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
